import { useState } from "react";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Tooltip state
// ---------------------------------------------------------------------------

interface TooltipInfo {
  x: number;
  y: number;
  label: string;
  value: number;
}

// ---------------------------------------------------------------------------
// Sparkline SVG com tooltip
// ---------------------------------------------------------------------------

interface SparklineProps {
  values: number[];
  labels?: string[];
  color?: string;
  className?: string;
  height?: number;
}

function fmt(n: number): string {
  return n.toLocaleString("pt-BR");
}

export function Sparkline({
  values,
  labels,
  color = "hsl(42 80% 57%)",
  className,
  height = 40,
}: SparklineProps): JSX.Element {
  const [tooltip, setTooltip] = useState<TooltipInfo | null>(null);

  if (values.length < 2) return <></>;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const w = 120;
  const h = height;
  const pad = 2;

  const pts = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * (w - pad * 2);
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return { x, y, v };
  });

  const polyline = pts.map((p) => `${p.x},${p.y}`).join(" ");
  const first = pts[0] ?? { x: pad, y: h - pad };
  const last = pts[pts.length - 1] ?? { x: w - pad, y: h - pad };
  const area = `${first.x},${first.y} ${polyline} ${last.x},${h - pad} ${pad},${h - pad}`;

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className={cn("overflow-visible", className)}
        aria-hidden="true"
        onMouseLeave={() => setTooltip(null)}
      >
        <defs>
          <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.25" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <polygon points={area} fill="url(#spark-fill)" />
        <polyline
          points={polyline}
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {/* Pontos interativos */}
        {pts.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={3}
            fill={color}
            opacity={0}
            className="cursor-crosshair"
            onMouseEnter={(e) => {
              const rect = (e.currentTarget.closest("svg") as SVGSVGElement).getBoundingClientRect();
              const svgEl = e.currentTarget.closest("svg") as SVGSVGElement;
              const viewW = svgEl.viewBox.baseVal.width;
              const scaleX = rect.width / viewW;
              setTooltip({
                x: p.x * scaleX,
                y: 0,
                label: labels?.[i] ?? `Dia ${i + 1}`,
                value: p.v,
              });
            }}
          />
        ))}
        {/* Overlay invisivel para captura fluida */}
        {pts.map((p, i) => (
          <rect
            key={`hit-${i}`}
            x={i === 0 ? 0 : (pts[i - 1]!.x + p.x) / 2}
            y={0}
            width={
              i === pts.length - 1
                ? w - (pts[i - 1] ? (pts[i - 1]!.x + p.x) / 2 : p.x)
                : ((pts[i + 1]!.x + p.x) / 2) - (i === 0 ? 0 : (pts[i - 1]!.x + p.x) / 2)
            }
            height={h}
            fill="transparent"
            className="cursor-crosshair"
            onMouseEnter={(e) => {
              const rect = (e.currentTarget.closest("svg") as SVGSVGElement).getBoundingClientRect();
              const svgEl = e.currentTarget.closest("svg") as SVGSVGElement;
              const viewW = svgEl.viewBox.baseVal.width;
              const scaleX = rect.width / viewW;
              setTooltip({
                x: p.x * scaleX,
                y: 0,
                label: labels?.[i] ?? `Dia ${i + 1}`,
                value: p.v,
              });
            }}
          />
        ))}
      </svg>

      {/* Tooltip flutuante */}
      {tooltip && (
        <div
          className="pointer-events-none absolute -top-9 z-10 -translate-x-1/2 rounded-md border border-[hsl(var(--border-strong))] bg-[hsl(var(--surface-raised))] px-2 py-1 shadow-md"
          style={{ left: tooltip.x }}
        >
          <p className="text-[10px] text-muted-foreground whitespace-nowrap">{tooltip.label}</p>
          <p className="text-xs font-bold tabular-nums text-foreground text-center">{fmt(tooltip.value)}</p>
        </div>
      )}
    </div>
  );
}
